import { refreshSession, signOut } from "@/services/auth";

// Public API endpoints that don't require authentication
const PUBLIC_ENDPOINTS = ["/agreements/questionnaire"];

const addAPIKey = (
  apiKey: string,
  request: RequestInfo | URL,
  config: RequestInit = {},
  impersonateCognitoId?: string,
) => {
  if (!config.headers) {
    config.headers = {};
  }

  const headers: Record<string, string> = {
    "X-API-key": apiKey,
  };

  if (impersonateCognitoId) {
    headers["X-Impersonate-Cognito-Id"] = impersonateCognitoId;
  }

  Object.assign(config.headers, headers);

  return [request, config] as const;
};

const isPublicEndpoint = (request: RequestInfo | URL): boolean => {
  try {
    let pathname: string;
    if (request instanceof Request) {
      pathname = new URL(request.url).pathname;
    } else if (request instanceof URL) {
      pathname = request.pathname;
    } else {
      pathname = new URL(request, window.location.origin).pathname;
    }
    return PUBLIC_ENDPOINTS.some(
      (endpoint) =>
        pathname === endpoint || pathname.startsWith(endpoint + "/"),
    );
  } catch {
    return false;
  }
};

let refreshPromise: Promise<string> | null = null;
const getNewAPIKey = async () => {
  if (refreshPromise) {
    return refreshPromise;
  }
  refreshPromise = (async () => {
    try {
      const session = await refreshSession();
      return session.getIdToken().getJwtToken();
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
};

export const AuthInterceptor = (
  idToken: string,
  getImpersonateCognitoId?: () => string | null,
) => {
  // Store the original fetch function
  const originalFetch = window.fetch;

  // Initialize the API key with the provided idToken.
  // It will be updated if the session is refreshed.
  let apiKey = idToken;

  // Override the global fetch function
  window.fetch = async (...args) => {
    const [request, config] = args;

    // Bypacss authentication for public endpoints
    if (isPublicEndpoint(request)) {
      return originalFetch(request, config);
    }

    const impersonateCognitoId = getImpersonateCognitoId?.() || undefined;
    const [requestWithAuth, configWithAuth] = addAPIKey(
      apiKey,
      request,
      config,
      impersonateCognitoId,
    );
    const response = await originalFetch(requestWithAuth, configWithAuth);

    // For unauthenticated users, attempt to refresh the session
    if (response.status === 401) {
      try {
        apiKey = await getNewAPIKey();
        const [retryRequest, retryConfig] = addAPIKey(
          apiKey,
          request,
          config,
          impersonateCognitoId,
        );
        const retryResponse = await originalFetch(retryRequest, retryConfig);
        if (retryResponse.status === 401) {
          signOut();
          return retryResponse;
        }
        return retryResponse;
      } catch (refreshError) {
        signOut();
        return response;
      }
    }

    // Return the response for successful calls or non-401 errors
    return response;
  };

  // Cleanup function to restore the original fetch
  return () => {
    window.fetch = originalFetch;
  };
};
